'use client';
import React, { useState, useEffect } from 'react';

import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { SignUpFormData } from './SignUpFlow';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

interface StepPaymentProps {
  formData: Partial<SignUpFormData>;
  onBack: () => void;
}

const MEMBERSHIP_FEE = 499;
const MAX_RETRY_ATTEMPTS = 3;
const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!);

// ─── Stripe error-code → human-readable message map ───────────────────────
const STRIPE_ERROR_MESSAGES: Record<string, string> = {
  card_declined: 'Your card was declined. Please use a different card or contact your bank.',
  insufficient_funds: 'Your card has insufficient funds. Please use a different payment method.',
  incorrect_cvc: 'The security code (CVC) you entered is incorrect. Please check and try again.',
  expired_card: 'Your card has expired. Please use a different card.',
  incorrect_number: 'The card number you entered is invalid. Please check and try again.',
  invalid_expiry_month: 'The expiry month is invalid. Please check your card details.',
  invalid_expiry_year: 'The expiry year is invalid. Please check your card details.',
  invalid_cvc: 'The CVC you entered is invalid. Please check your card details.',
  processing_error: 'An error occurred while processing your card. Please try again in a moment.',
  do_not_honor: 'Your bank declined the transaction. Please contact your bank or use a different card.',
  fraudulent: 'This transaction was flagged as potentially fraudulent. Please contact your bank.',
  lost_card: 'This card has been reported lost. Please use a different payment method.',
  stolen_card: 'This card has been reported stolen. Please use a different payment method.',
  card_velocity_exceeded: 'Too many attempts with this card. Please wait a moment or use a different card.',
  authentication_required: 'Your bank requires additional authentication. Please complete the verification.',
  payment_intent_authentication_failure: 'Payment authentication failed. Please try again.',
  payment_intent_incompatible_payment_method: 'This payment method is not supported. Please use a different one.',
};

function getStripeErrorMessage(error: { code?: string; decline_code?: string; message?: string }): string {
  if (error.decline_code && STRIPE_ERROR_MESSAGES[error.decline_code]) {
    return STRIPE_ERROR_MESSAGES[error.decline_code];
  }
  if (error.code && STRIPE_ERROR_MESSAGES[error.code]) {
    return STRIPE_ERROR_MESSAGES[error.code];
  }
  return error.message || 'Payment failed. Please try again.';
}

function isNetworkError(err: unknown): boolean {
  if (err instanceof Error) {
    return (
      err.message.toLowerCase().includes('network') ||
      err.message.toLowerCase().includes('fetch') ||
      err.message.toLowerCase().includes('connection') ||
      err.message.toLowerCase().includes('timeout')
    );
  }
  return false;
}

// ─── Error Banner ──────────────────────────────────────────────────────────
function PaymentErrorBanner({
  message,
  onDismiss,
  retryCount,
}: {
  message: string;
  onDismiss: () => void;
  retryCount: number;
}) {
  return (
    <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-xl p-4 mb-4 flex items-start gap-3">
      <Icon name="ExclamationCircleIcon" size={20} className="text-[#EF4444] flex-shrink-0 mt-0.5" variant="solid" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-[#FCA5A5] font-medium">Payment Failed</p>
        <p className="text-xs text-[#9CA3AF] mt-1 leading-relaxed">{message}</p>
        {retryCount >= MAX_RETRY_ATTEMPTS && (
          <p className="text-xs text-[#F59E0B] mt-2">
            Multiple attempts failed. Please try a different payment method or contact support.
          </p>
        )}
      </div>
      <button
        onClick={onDismiss}
        className="text-[#6B7280] hover:text-white transition-colors flex-shrink-0"
        aria-label="Dismiss error"
      >
        <Icon name="XMarkIcon" size={16} />
      </button>
    </div>
  );
}

// ─── Success Screen ────────────────────────────────────────────────────────
function PaymentSuccessScreen({
  fullName,
  referralCode,
  membershipNumber,
  email,
}: {
  fullName: string;
  referralCode: string;
  membershipNumber: number;
  email: string;
}) {
  const [countdown, setCountdown] = React.useState(5);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(timer);
          window.location.href = '/member-dashboard';
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="text-center py-4">
      <div className="w-16 h-16 rounded-full bg-[#10B981]/20 border border-[#10B981]/40 flex items-center justify-center mx-auto mb-6">
        <Icon name="CheckCircleIcon" size={36} className="text-[#10B981]" variant="solid" />
      </div>

      <h2 className="font-display font-bold text-2xl text-white mb-2">
        Welcome, {fullName}! 🎉
      </h2>
      <p className="text-[#9CA3AF] text-sm mb-6">
        You are Member #{membershipNumber} of 500 founding members.
      </p>

      {/* Referral code */}
      <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-5 mb-5 text-left">
        <p className="text-xs text-[#6B7280] font-mono uppercase tracking-wider mb-2">
          Your Referral Code
        </p>
        <p className="font-mono font-bold text-3xl text-[#F59E0B] tracking-widest">
          {referralCode}
        </p>
        <p className="text-xs text-[#6B7280] mt-2">
          Share this code with truck and pickup owners to start earning.
        </p>
      </div>

      {/* Email confirmation notice */}
      <div className="bg-[#10B981]/10 border border-[#10B981]/20 rounded-xl p-4 mb-6 flex items-start gap-3 text-left">
        <Icon name="EnvelopeIcon" size={18} className="text-[#10B981] flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-[#10B981] font-medium">Confirmation email sent</p>
          <p className="text-xs text-[#9CA3AF] mt-0.5">
            A welcome email with your referral code and dashboard link has been sent to{' '}
            <span className="text-white font-mono">{email}</span>.
          </p>
        </div>
      </div>

      <button
        onClick={() => { window.location.href = '/member-dashboard'; }}
        className="btn-primary w-full justify-center py-4 text-base mb-3"
      >
        <Icon name="ArrowRightIcon" size={20} />
        Go to My Dashboard
      </button>

      <p className="text-xs text-[#6B7280]">
        Redirecting automatically in {countdown}s…
      </p>
    </div>
  );
}

// ─── Payment Form ──────────────────────────────────────────────────────────
function PaymentForm({
  clientSecret,
  paymentIntentId,
  formData,
  authUserId,
  onSuccess,
}: {
  clientSecret: string;
  paymentIntentId: string;
  formData: Partial<SignUpFormData>;
  authUserId: string;
  onSuccess: (referralCode: string, membershipNumber: number) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isElementReady, setIsElementReady] = useState(false);
  const supabase = createClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPaymentError(null);

    // ── Pre-submit guards ──────────────────────────────────────────────────
    if (!stripe) {
      setPaymentError('Stripe has not loaded yet. Please wait a moment and try again.');
      return;
    }
    if (!elements) {
      setPaymentError('Payment form is not ready. Please refresh the page and try again.');
      return;
    }
    if (!isElementReady) {
      setPaymentError('Please wait for the payment form to finish loading.');
      return;
    }
    if (retryCount >= MAX_RETRY_ATTEMPTS) {
      setPaymentError(
        'Maximum retry attempts reached. Please refresh the page or use a different payment method.'
      );
      return;
    }

    setIsProcessing(true);

    try {
      // ── Validate card fields before submitting ─────────────────────────
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setPaymentError(getStripeErrorMessage(submitError));
        setRetryCount((c) => c + 1);
        setIsProcessing(false);
        return;
      }

      // ── Confirm payment ────────────────────────────────────────────────
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/member-dashboard`,
        },
        redirect: 'if_required',
      });

      if (error) {
        const msg = getStripeErrorMessage(error);
        setPaymentError(msg);
        toast.error(msg);
        setRetryCount((c) => c + 1);
        setIsProcessing(false);
        return;
      }

      // ── Guard unexpected payment intent statuses ───────────────────────
      if (!paymentIntent) {
        setPaymentError('No payment confirmation received. Please check your email or contact support.');
        setIsProcessing(false);
        return;
      }

      if (paymentIntent.status === 'requires_action') {
        setPaymentError('Your bank requires additional authentication. Please complete the 3D Secure verification.');
        setIsProcessing(false);
        return;
      }

      if (paymentIntent.status === 'requires_payment_method') {
        setPaymentError('Payment was not completed. Please enter a valid payment method and try again.');
        setRetryCount((c) => c + 1);
        setIsProcessing(false);
        return;
      }

      if (paymentIntent.status === 'canceled') {
        setPaymentError('This payment session has been cancelled. Please go back and start a new checkout.');
        setIsProcessing(false);
        return;
      }

      if (paymentIntent.status !== 'succeeded') {
        setPaymentError(
          `Unexpected payment status: "${paymentIntent.status}". Please contact support if your card was charged.`
        );
        setIsProcessing(false);
        return;
      }

      // ── Payment succeeded — confirm with backend ───────────────────────
      const { data, error: confirmError } = await supabase.functions.invoke(
        'confirm-membership-payment',
        {
          body: {
            paymentIntentId: paymentIntent.id,
            authUserId,
            memberData: {
              fullName: formData.fullName,
              email: formData.email,
              phone: formData.phone,
              bankName: formData.bankName,
              accountHolderName: formData.accountHolderName,
              accountNumber: formData.accountNumber,
              bankBranch: formData.bankBranch,
            },
          },
        }
      );

      if (confirmError) {
        // Payment went through but account setup failed — critical: tell user not to retry
        setPaymentError(
          'Your payment was successful, but account setup encountered an error. ' + 'Do NOT retry — please contact support with your email address and we will activate your account manually.'
        );
        toast.error('Account setup failed after payment. Please contact support.');
        setIsProcessing(false);
        return;
      }

      toast.success('Payment successful! Welcome to BidTrek Partner Network!');
      onSuccess(data?.referralCode ?? '', data?.membershipNumber ?? 1);
    } catch (err: unknown) {
      const networkErr = isNetworkError(err);
      const msg = networkErr
        ? 'A network error occurred. Please check your internet connection and try again.' : (err instanceof Error ? err.message : null) ||'Payment failed. Please try again.';
      setPaymentError(msg);
      toast.error(msg);
      if (!networkErr) setRetryCount((c) => c + 1);
    } finally {
      setIsProcessing(false);
    }
  };

  const attemptsLeft = MAX_RETRY_ATTEMPTS - retryCount;
  const isBlocked = retryCount >= MAX_RETRY_ATTEMPTS;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {paymentError && (
        <PaymentErrorBanner
          message={paymentError}
          onDismiss={() => setPaymentError(null)}
          retryCount={retryCount}
        />
      )}

      {retryCount > 0 && retryCount < MAX_RETRY_ATTEMPTS && (
        <div className="flex items-center gap-2 text-xs text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/20 rounded-lg px-3 py-2">
          <Icon name="ExclamationTriangleIcon" size={14} className="flex-shrink-0" />
          <span>
            Attempt {retryCount} of {MAX_RETRY_ATTEMPTS}. {attemptsLeft} attempt{attemptsLeft !== 1 ? 's' : ''} remaining.
          </span>
        </div>
      )}

      <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-4">
        <PaymentElement
          options={{ layout: 'tabs' }}
          onReady={() => setIsElementReady(true)}
          onChange={(e) => {
            // Clear error when user starts correcting their card details
            if (e.complete && paymentError) setPaymentError(null);
          }}
        />
      </div>

      <button
        type="submit"
        disabled={!stripe || isProcessing || isBlocked || !isElementReady}
        className="btn-primary w-full justify-center py-4 text-base disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isProcessing ? (
          <>
            <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
            Processing Payment...
          </>
        ) : isBlocked ? (
          <>
            <Icon name="XCircleIcon" size={20} />
            Too Many Failed Attempts
          </>
        ) : (
          <>
            <Icon name="LockClosedIcon" size={20} />
            Pay EC${MEMBERSHIP_FEE} & Activate Membership
          </>
        )}
      </button>

      <div className="flex items-center justify-center gap-2 text-xs text-[#6B7280]">
        <Icon name="ShieldCheckIcon" size={14} className="text-[#6B7280]" />
        Secured by Stripe. Your payment details are encrypted.
      </div>
    </form>
  );
}

// ─── Outer Step ────────────────────────────────────────────────────────────
export default function StepPayment({ formData, onBack }: StepPaymentProps) {
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);
  const [intentError, setIntentError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [authUserId, setAuthUserId] = useState<string | null>(null);
  const [spotsRemaining, setSpotsRemaining] = useState<number | null>(null);
  const [successData, setSuccessData] = useState<{ referralCode: string; membershipNumber: number } | null>(null);
  const { signUp, signIn } = useAuth();
  const supabase = createClient();

  React.useEffect(() => {
    supabase
      .from('spots_counter')
      .select('total_spots, spots_taken')
      .eq('id', 1)
      .single()
      .then(({ data }) => {
        if (data) setSpotsRemaining(data.total_spots - data.spots_taken);
      });
  }, []);

  // ── Validate required form data before attempting payment init ───────────
  const validateFormData = (): string | null => {
    if (!formData.fullName?.trim()) return 'Full name is missing. Please go back and complete Step 1.';
    if (!formData.email?.trim()) return 'Email address is missing. Please go back and complete Step 1.';
    if (!formData.phone?.trim()) return 'Phone number is missing. Please go back and complete Step 1.';
    if (!formData.bankName?.trim()) return 'Bank name is missing. Please go back and complete Step 2.';
    if (!formData.accountHolderName?.trim())
      return 'Account holder name is missing. Please go back and complete Step 2.';
    if (!formData.accountNumber?.trim())
      return 'Account number is missing. Please go back and complete Step 2.';
    return null;
  };

  const initializePayment = async () => {
    if (clientSecret) return;

    const validationError = validateFormData();
    if (validationError) {
      setIntentError(validationError);
      return;
    }

    setIntentError(null);
    setIsCreatingIntent(true);

    try {
      // ── Create auth account ──────────────────────────────────────────────
      let userId = authUserId;
      if (!userId) {
        const tempPassword = Math.random().toString(36).slice(-10) + 'A1!';
        let authData: Awaited<ReturnType<typeof signUp>>;
        try {
          authData = await signUp(formData.email!, tempPassword, {
            fullName: formData.fullName,
          });
        } catch (authErr: unknown) {
          const msg = authErr instanceof Error ? authErr.message : '';
          if (msg.toLowerCase().includes('already registered') || msg.toLowerCase().includes('already exists')) {
            throw new Error(
              'An account with this email already exists. Please log in or use a different email address.'
            );
          }
          throw new Error('Failed to create your account. Please try again.');
        }

        userId = authData?.user?.id ?? null;
        if (!userId) throw new Error('Account creation failed. Please try again.');
        setAuthUserId(userId);

        await signIn(formData.email!, tempPassword);
      }

      // ── Create Stripe payment intent ─────────────────────────────────────
      // Resolve effective bank name (handle "Other" selection)
      const effectiveBankName =
        formData.bankName === 'Other'
          ? formData.otherBankName?.trim() || 'Other'
          : formData.bankName ?? '';

      const { data, error } = await supabase.functions.invoke('create-membership-payment', {
        body: {
          memberData: {
            fullName: formData.fullName,
            email: formData.email,
            phone: formData.phone,
            bankName: effectiveBankName,
            bankAccountName: formData.accountHolderName,
            bankBranch: formData.bankBranch,
          },
        },
      });

      if (error) {
        throw new Error('Payment service is temporarily unavailable. Please try again in a moment.');
      }
      if (!data?.clientSecret) {
        throw new Error(data?.error || 'Failed to initialize payment. Please try again.');
      }

      setClientSecret(data.clientSecret);
      setPaymentIntentId(data.paymentIntentId);
    } catch (err: unknown) {
      const networkErr = isNetworkError(err);
      const msg = networkErr
        ? 'A network error occurred while setting up payment. Please check your connection and try again.'
        : (err instanceof Error ? err.message : null) || 'Failed to initialize payment. Please try again.';
      setIntentError(msg);
      toast.error(msg);
    } finally {
      setIsCreatingIntent(false);
    }
  };

  const handleSuccess = (referralCode: string, membershipNumber: number) => {
    setSuccessData({ referralCode, membershipNumber });
  };

  // ── Show success confirmation screen after payment ─────────────────────
  if (successData) {
    return (
      <PaymentSuccessScreen
        fullName={formData.fullName ?? ''}
        referralCode={successData.referralCode}
        membershipNumber={successData.membershipNumber}
        email={formData.email ?? ''}
      />
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h2 className="font-display font-bold text-2xl text-white mb-2">Complete Your Membership</h2>
        <p className="text-[#9CA3AF] text-sm">
          Review your order and complete payment to activate your membership.
        </p>
      </div>

      {/* Order summary */}
      <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-[#2A2A2A]">
          <h3 className="font-display font-semibold text-white">BidTrek Partner Network</h3>
          <span className="badge-new">Founding Member</span>
        </div>

        <div className="space-y-3 mb-5">
          {[
            'Lifetime membership — no renewals',
            'Unique referral code + shareable link',
            'Monthly automatic payouts',
            'Real-time earnings dashboard',
            'Driver application portal access',
          ].map((benefit) => (
            <div key={`benefit-${benefit}`} className="flex items-center gap-3">
              <Icon name="CheckCircleIcon" size={16} className="text-[#10B981] flex-shrink-0" variant="solid" />
              <span className="text-sm text-[#9CA3AF]">{benefit}</span>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between p-3 bg-[#F59E0B]/10 border border-[#F59E0B]/20 rounded-lg mb-4">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444] animate-pulse" />
            <span className="text-xs text-[#9CA3AF] font-mono">Spots remaining:</span>
          </div>
          <span className="font-mono font-bold text-[#F59E0B]">
            {spotsRemaining !== null ? `${spotsRemaining} of 500` : '...'}
          </span>
        </div>

        <div className="border-t border-[#2A2A2A] pt-4">
          <div className="flex items-center justify-between">
            <span className="text-[#9CA3AF] text-sm">One-time membership fee</span>
            <span className="font-mono font-bold text-2xl text-white">EC${MEMBERSHIP_FEE}</span>
          </div>
          <p className="text-xs text-[#6B7280] mt-1">No recurring charges. Ever.</p>
        </div>
      </div>

      {/* Account summary */}
      <div className="bg-[#111111] border border-[#2A2A2A] rounded-xl p-4 mb-6 space-y-2">
        <p className="text-xs text-[#6B7280] font-mono uppercase tracking-wider mb-3">Your Account</p>
        <div className="flex justify-between text-sm">
          <span className="text-[#6B7280]">Name</span>
          <span className="text-white font-medium">{formData.fullName || '—'}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-[#6B7280]">Email</span>
          <span className="text-white font-mono text-xs">{formData.email || '—'}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-[#6B7280]">Bank</span>
          <span className="text-white">{formData.bankName || '—'}</span>
        </div>
      </div>

      {/* Intent-level error banner */}
      {intentError && (
        <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-xl p-4 mb-4 flex items-start gap-3">
          <Icon name="ExclamationCircleIcon" size={20} className="text-[#EF4444] flex-shrink-0 mt-0.5" variant="solid" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-[#FCA5A5] font-medium">Setup Error</p>
            <p className="text-xs text-[#9CA3AF] mt-1 leading-relaxed">{intentError}</p>
          </div>
          <button
            onClick={() => setIntentError(null)}
            className="text-[#6B7280] hover:text-white transition-colors flex-shrink-0"
            aria-label="Dismiss error"
          >
            <Icon name="XMarkIcon" size={16} />
          </button>
        </div>
      )}

      {/* Payment section */}
      {!clientSecret ? (
        <button
          onClick={initializePayment}
          disabled={isCreatingIntent}
          className="btn-primary w-full justify-center py-4 text-base mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isCreatingIntent ? (
            <>
              <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
              Preparing Payment...
            </>
          ) : (
            <>
              <Icon name="LockClosedIcon" size={20} />
              Pay EC${MEMBERSHIP_FEE} & Activate Membership
            </>
          )}
        </button>
      ) : (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: {
              theme: 'night',
              variables: {
                colorPrimary: '#F59E0B',
                colorBackground: '#1A1A1A',
                colorText: '#FFFFFF',
                colorDanger: '#EF4444',
                borderRadius: '8px',
              },
            },
          }}
        >
          <PaymentForm
            clientSecret={clientSecret}
            paymentIntentId={paymentIntentId!}
            formData={formData}
            authUserId={authUserId!}
            onSuccess={handleSuccess}
          />
        </Elements>
      )}

      <button type="button" onClick={onBack} className="btn-secondary w-full justify-center py-3 mt-3">
        <Icon name="ArrowLeftIcon" size={18} />
        Back to Bank Details
      </button>
    </div>
  );
}